import { useEffect, useState } from 'react';

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { StatusCard } from '@/components/ui/card/StatusCard';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTranslation } from '@/hooks/useTranslation';
import type { AdminApiClient, AdminUser } from '@/features/admin/api/AdminApiClient';

export type AssignableRole = 'user' | 'admin';

export type MemberFormDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  member?: AdminUser;
  companies: string[];
  modules: string[];
  client: AdminApiClient;
  onSuccess: () => void;
  isSelf?: boolean;
  /** Roles the caller is allowed to assign; a single entry renders a read-only role. */
  allowedRoles?: AssignableRole[];
};

export function MemberFormDialog({
  open,
  onOpenChange,
  mode,
  member,
  companies,
  modules,
  client,
  onSuccess,
  isSelf,
  allowedRoles = ['user', 'admin'],
}: MemberFormDialogProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'user' | 'admin'>('user');
  const [companyName, setCompanyName] = useState('');
  const [selectedModules, setSelectedModules] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Re-seed the form whenever it opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    if (mode === 'edit' && member) {
      setEmail(member.email);
      setFirstName(member.firstName ?? '');
      setLastName(member.lastName ?? '');
      setRole(member.role === 'admin' ? 'admin' : 'user');
      setCompanyName(member.companyName);
      setSelectedModules(member.modules);
    } else {
      setEmail('');
      setPassword('');
      setFirstName('');
      setLastName('');
      setRole('user');
      setCompanyName(companies[0] ?? '');
      setSelectedModules([]);
    }
  }, [open, mode, member, companies]);

  function toggleModule(key: string) {
    setSelectedModules((prev) =>
      prev.includes(key) ? prev.filter((m) => m !== key) : [...prev, key]
    );
  }

  // Clamp to the caller's ceiling — the backend enforces it too; this keeps the UI honest.
  const effectiveRole = allowedRoles.includes(role) ? role : (allowedRoles[0] ?? 'user');

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    const res =
      mode === 'create'
        ? await client.createUser({
            email,
            password,
            firstName,
            lastName,
            role: effectiveRole,
            modules: selectedModules,
            companyName,
          })
        : await client.updateUser(member!.email, {
            ...(isSelf ? {} : { role: effectiveRole }),
            companyName,
            modules: selectedModules,
            firstName,
            lastName,
          });
    setSubmitting(false);
    if (res.ok) {
      onSuccess();
      onOpenChange(false);
    } else {
      setError(res.message ?? t('administration.errors.generic'));
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? t('administration.member.addTitle') : t('administration.member.editTitle')}
          </DialogTitle>
          <DialogDescription>
            {mode === 'edit' ? member?.email : t('administration.member.addSubtitle')}
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          {mode === 'create' && (
            <>
              <div className="flex flex-col gap-2">
                <Label htmlFor="member-email">{t('administration.member.email')}</Label>
                <Input
                  id="member-email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="flex flex-col gap-2">
                <Label htmlFor="member-password">{t('administration.member.password')}</Label>
                <Input
                  id="member-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="member-first">{t('administration.member.firstName')}</Label>
              <Input
                id="member-first"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="member-last">{t('administration.member.lastName')}</Label>
              <Input
                id="member-last"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
              />
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label>{t('administration.member.role')}</Label>
            {isSelf ? (
              <>
                <div className="flex items-center rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                  {t('administration.roles.superAdmin')}
                </div>
                <p className="text-xs text-muted-foreground">{t('administration.member.roleHint')}</p>
              </>
            ) : allowedRoles.length <= 1 ? (
              <div className="flex items-center rounded-md border border-input bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
                {t(`administration.roles.${allowedRoles[0] ?? 'user'}`)}
              </div>
            ) : (
              <Select value={effectiveRole} onValueChange={(v) => setRole(v as AssignableRole)}>
                <SelectTrigger aria-label={t('administration.member.role')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {allowedRoles.map((r) => (
                    <SelectItem key={r} value={r}>
                      {t(`administration.roles.${r}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {!isSelf && <p className="text-xs text-muted-foreground">{t('administration.member.roleHint')}</p>}
          </div>

          <div className="flex flex-col gap-2">
            <Label>{t('administration.member.company')}</Label>
            <Select value={companyName} onValueChange={setCompanyName}>
              <SelectTrigger aria-label={t('administration.member.company')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label>{t('administration.member.access')}</Label>
            <div className="flex flex-wrap gap-2">
              {modules.map((key) => (
                <label
                  key={key}
                  htmlFor={`mod-${key}`}
                  className="flex cursor-pointer items-center gap-2 rounded-lg border border-input px-3 py-2 text-sm"
                >
                  <Checkbox
                    id={`mod-${key}`}
                    checked={selectedModules.includes(key)}
                    onCheckedChange={() => toggleModule(key)}
                  />
                  {t(`modules.labels.${key}`)}
                </label>
              ))}
            </div>
          </div>

          {error && <StatusCard type="error" message={error} />}
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button variant="default" size="sm" onClick={handleSubmit} loading={submitting}>
            {mode === 'create' ? t('administration.member.add') : t('administration.member.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
